import React from "react";
import { normalizeKey } from "@bookingcom/bui-core/utilities/helpers";
import { changeMonth, changeDate, castDateToISO, castISOToDate, isDateDisabled, isMaxMonth, isMinMonth, } from "@bookingcom/bui-core/utilities/date";
import keys from "@bookingcom/bui-core/constants/keys";
import { Context } from "./Calendar.context.js";
var useCalendarNavigation = function (props) {
    var onBaseMonthChange = props.onBaseMonthChange, baseMonthDate = props.baseMonthDate, elContentRef = props.elContentRef, elVerticalBaseMonthRef = props.elVerticalBaseMonthRef, nextControlRef = props.nextControlRef, previousControlRef = props.previousControlRef;
    var _a = React.useContext(Context), startDate = _a.startDate, endDate = _a.endDate, minDate = _a.minDate, maxDate = _a.maxDate, maxSelectionLength = _a.maxSelectionLength, enabledDates = _a.enabledDates, disabledDates = _a.disabledDates;
    var arrowNavigationModeRef = React.useRef(false);
    var focusedDateISORef = React.useRef(null);
    var _b = React.useState(null), focusedDateISO = _b[0], setFocusedDateISO = _b[1];
    var dateWrapperEl = elVerticalBaseMonthRef.current || elContentRef.current;
    var firstDateEl = dateWrapperEl === null || dateWrapperEl === void 0 ? void 0 : dateWrapperEl.querySelector("[data-date]");
    var firstDateISO = (firstDateEl === null || firstDateEl === void 0 ? void 0 : firstDateEl.getAttribute("data-date")) || null;
    var getDateElement = React.useCallback(function (dateISO) {
        if (!elContentRef.current)
            return;
        return elContentRef.current.querySelector("[data-date=\"".concat(dateISO, "\"]"));
    }, [elContentRef]);
    var getFocusedDateElement = React.useCallback(function () {
        if (!focusedDateISORef.current)
            return;
        return getDateElement(focusedDateISORef.current);
    }, [getDateElement]);
    var handlePreviousClick = function () {
        var _a;
        var previousMonth = changeMonth(baseMonthDate, -1);
        var minimum = isMinMonth(previousMonth, minDate);
        if (minimum) {
            (_a = nextControlRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
        arrowNavigationModeRef.current = false;
        onBaseMonthChange(previousMonth);
    };
    var handleNextClick = function () {
        var _a;
        var nextMonth = changeMonth(baseMonthDate, 1);
        var maximum = isMaxMonth(nextMonth, maxDate);
        if (maximum) {
            (_a = previousControlRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
        arrowNavigationModeRef.current = false;
        onBaseMonthChange(nextMonth);
    };
    var handleDayKeyDown = function (event) {
        var _a;
        var key = normalizeKey(event.key);
        if (![
            keys.LEFT,
            keys.RIGHT,
            keys.UP,
            keys.DOWN,
            keys.SPACE,
            keys.ENTER,
            keys.PAGE_DOWN,
            keys.PAGE_UP,
        ].includes(key)) {
            arrowNavigationModeRef.current = false;
            return;
        }
        var currentFocusDateISO = event.currentTarget.getAttribute("data-date");
        if (!currentFocusDateISO)
            return;
        event.preventDefault();
        arrowNavigationModeRef.current = true;
        var currentFocusDate = castISOToDate(currentFocusDateISO);
        var targetFocusDate;
        switch (key) {
            case keys.RIGHT:
                targetFocusDate = changeDate(currentFocusDate, +1);
                break;
            case keys.LEFT:
                targetFocusDate = changeDate(currentFocusDate, -1);
                break;
            case keys.DOWN:
                targetFocusDate = changeDate(currentFocusDate, +7);
                break;
            case keys.UP:
                targetFocusDate = changeDate(currentFocusDate, -7);
                break;
            case keys.PAGE_DOWN:
                targetFocusDate = changeMonth(currentFocusDate, +1);
                break;
            case keys.PAGE_UP:
                targetFocusDate = changeMonth(currentFocusDate, -1);
                break;
            case keys.SPACE:
            case keys.ENTER:
                (_a = getFocusedDateElement()) === null || _a === void 0 ? void 0 : _a.click();
                break;
            default:
                break;
        }
        if (targetFocusDate) {
            var targetFocusDateISO = castDateToISO(targetFocusDate);
            var targetEl = getDateElement(targetFocusDateISO);
            var isDisabled = isDateDisabled({
                isoDate: targetFocusDateISO,
                minDate: minDate,
                maxDate: maxDate,
                enabledDates: enabledDates,
                disabledDates: disabledDates,
                maxSelectionLength: maxSelectionLength,
                startDate: startDate,
                endDate: endDate,
            });
            if (isDisabled)
                return;
            setFocusedDateISO(targetFocusDateISO);
            focusedDateISORef.current = targetFocusDateISO;
            if (targetEl) {
                targetEl.focus();
                return;
            }
            onBaseMonthChange(targetFocusDate);
        }
    };
    React.useEffect(function () {
        if (!arrowNavigationModeRef.current)
            return;
        var targetEl = getFocusedDateElement();
        targetEl === null || targetEl === void 0 ? void 0 : targetEl.focus();
    }, [baseMonthDate, getFocusedDateElement]);
    return {
        handleNextClick: handleNextClick,
        handlePreviousClick: handlePreviousClick,
        handleDayKeyDown: handleDayKeyDown,
        firstDateISO: firstDateISO,
        focusedDateISO: focusedDateISO,
    };
};
export default useCalendarNavigation;
